import * as React from 'react';

import {
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from 'reactstrap';

import { PortalTextMainHamburgerMenu } from '../../Models/Api/strongbox.financialportal';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCheck,
    faChevronDown,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';

import '../Main.scss';

type Props = {
    logout?: () => void;
    settings?: () => void;
    dataCollectionReport?: () => void;
    notificationSettings?: () => void;
    onLanguageCodeChange?: (languageCode: string) => void;
    canAccessSettings: boolean;
    canAccessGlobalSubmissions: boolean;
    canChangeLanguage: boolean;
    hamburgerText?: PortalTextMainHamburgerMenu;
    activeLanguageCode: string;
    borderless?: boolean;
};

export const FinPortalDropdown: React.FC<Props> = (props): React.ReactElement => {
    const {
        hamburgerText,
        onLanguageCodeChange,
        activeLanguageCode,
        borderless
    } = props;

    const [dropdownOpen, setDropdownOpen] = React.useState<boolean>(false);
    const [languageDropdownOpen, setLanguageDropdownOpen] = React.useState<boolean>(false);

    const checkIcon: React.ReactNode = (
        <React.Fragment>
            <FontAwesomeIcon icon={faCheck}  />
            <div className={'icon-margin'} />
        </React.Fragment>
    );

    const languageDropdownItems = React.useMemo(() => {
        if (!hamburgerText) {
            return (<></>);
        }

        const result: React.ReactNode[] = [];

        hamburgerText.portalLanguages.forEach((language, index) => {
            result.push((
                <DropdownItem
                    key={`dropdown-language-selection-${index}`}
                    onClick={() => { !!onLanguageCodeChange && !!language.languageCode && onLanguageCodeChange(language.languageCode)}}
                >
                    {activeLanguageCode === language.languageCode ? (checkIcon) : (<div className={'basic-icon-size'}></div>) }
                    {language.description}
                </DropdownItem>
            ));
        });

        return result;

        // nothing needed in dependencies beyond hamburgerText
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [hamburgerText]);

    return (
        <Dropdown
            className={`portal-options-dropdown ${ borderless === true ? 'dropdown-borderless' : '' }`}
            isOpen={dropdownOpen || languageDropdownOpen}
            toggle={() => {
                setDropdownOpen(!dropdownOpen);
            }}
        >
            <DropdownToggle>
                <FontAwesomeIcon icon={faChevronDown} />
            </DropdownToggle>
            <DropdownMenu
                className={'portal-options-dropdown-menu'}
                end
            >
                {
                    props.canAccessSettings && (
                        <>
                            <DropdownItem
                                onClick={() => { props.settings && props.settings() }}
                            >
                                { hamburgerText?.userManagement ?? '' }
                            </DropdownItem>
                        </>
                    )
                }
                {
                    props.canAccessGlobalSubmissions && (
                        <>
                            <DropdownItem
                                onClick={() => { props.dataCollectionReport && props.dataCollectionReport() }}
                            >
                                { hamburgerText?.dataCollectionReport ?? ''}
                            </DropdownItem>
                        </>
                    )
                }
                <DropdownItem
                    onClick={() => { props.notificationSettings && props.notificationSettings() }}
                >
                    { hamburgerText?.notificationSettings ?? '' }
                </DropdownItem>
                {
                    props.canChangeLanguage && !!hamburgerText && (
                        <>
                            <Dropdown
                                className={'portal-options-dropdown'}
                                isOpen={languageDropdownOpen}
                                toggle={() => { 
                                    setLanguageDropdownOpen(!languageDropdownOpen);
                                }}
                            >
                                <DropdownToggle 
                                    className={'submenu-item'} 
                                    caret
                                >
                                    {hamburgerText?.portalLanguage || ''}
                                </DropdownToggle>
                                <DropdownMenu
                                    className={languageDropdownOpen ? 'portal-options-dropdown-menu submenu' : ''}
                                    end
                                >
                                    {
                                        languageDropdownItems
                                    }
                                </DropdownMenu>
                            </Dropdown>
                        </>
                    )
                }
                <DropdownItem
                    className={'portal-options-dropdown-divider'}
                    divider
                />
                <DropdownItem
                    onClick={() => { props.logout && props.logout() }}
                >
                    { hamburgerText?.logout ?? '' }
                </DropdownItem>
            </DropdownMenu>
        </Dropdown>
    );
}
